import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { KEY_OWN_CID, KEY_TOKEN } from "./challengeModel";
import { ProcessedProgress, SimplifiedProgress, simplifyProgress, getProcessedProgress } from "./progressModel";
import deepEquals from "../util/deepEquals";
import { getShareKeysAndLabels, getSpreadKeysAndLabels, getValueKeysAndLabels } from "./RegisterProgress";
import { useTranslation } from "react-i18next";


export const useChallenge = () => {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [valueKeysAndLabels, setValueKeysAndLabels] = useState(getValueKeysAndLabels(t));
  const [spreadKeysAndLabels, setSpreadKeysAndLabels] = useState(getSpreadKeysAndLabels(t));
  const [shareKeysAndLabels, setShareKeysAndLabels] = useState(getShareKeysAndLabels(t));
  const [name, setName] = useState("");
  const [progress, setProgress] = useState({});
  const [referralCount, setReferralCount] = useState(0);
  const [createdAtEpoch, setCreatedAtEpoch] = useState(0);
  const [directReferralCount, setDirectReferralCount] = useState(0);
  const [simplifiedProgress, setSimplifiedProgress] = useState<null | SimplifiedProgress>(null);
  const [processedProgress, setProcessedProgress] = useState<null | ProcessedProgress>(null);
  const [updatedAtEpoch, setUpdatedAtEpoch] = useState(0);
  const [thumbAtEpoch, setThumbAtEpoch] = useState(0);
  const [censoredEmail, setCensoredEmail] = useState<string | null>(null);

  const [ownCid, setOwnCid] = useState(() => {
    return localStorage.getItem(KEY_OWN_CID) || '';
  });

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === KEY_OWN_CID) {
        setOwnCid(e.newValue || '');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const getDaysSinceJoined = (createdAtMs: number) => {
    const now = Date.now();
    return Math.floor((now - createdAtMs) / (1000 * 60 * 60 * 24));
  }

  useEffect(() => {
    const valueKeysAndLabels = getValueKeysAndLabels(t);
    const spreadKeysAndLabels = getSpreadKeysAndLabels(t);
    const shareKeysAndLabels = getShareKeysAndLabels(t);
    setValueKeysAndLabels(valueKeysAndLabels);
    setSpreadKeysAndLabels(spreadKeysAndLabels);
    setShareKeysAndLabels(shareKeysAndLabels);
    setProcessedProgress(getProcessedProgress(
      simplifiedProgress, referralCount, getDaysSinceJoined(createdAtEpoch),
      valueKeysAndLabels, spreadKeysAndLabels, 
      shareKeysAndLabels, t
    ));
  }, [t])

  const cidToGet = cid || ownCid;

  const convertToEpoch = (datetime: string | null): number => {
    if (!datetime) return 0;
    const date = new Date(datetime);
    return isNaN(date.getTime()) ? 0 : date.getTime();
  };

  const safeParse = (json) => {
    try {
      return JSON.parse(json);
    } catch (err) {
      console.error('Failed to parse json: ', json, err);
      return undefined;
    }
  }

  const processProgress = (progress, newReferralCount, daysSinceJoined) => {
    const pr = simplifyProgress(progress);
    if (deepEquals(pr, simplifiedProgress) && referralCount == newReferralCount) {
      return;
    }
    setSimplifiedProgress(pr);
    setReferralCount(newReferralCount);
    setProcessedProgress(getProcessedProgress(
      pr, newReferralCount, daysSinceJoined,
      valueKeysAndLabels, spreadKeysAndLabels, 
      shareKeysAndLabels, t
    ));
  }

  const loadChallenge = () => {
    if (!cidToGet) return;

    const formData: Record<string, string> = {
      cid: cidToGet,
      token: localStorage.getItem(KEY_TOKEN)
    };

    fetch('/api/get_challenge.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams(formData)
    })
      .then(response => response.json())
      .then(data => {
        if (data.name) {
          setName(data.name);
          const progress = safeParse(data.progress);
          setProgress(progress);
          const createdAtMs = convertToEpoch(data.created_at);
          processProgress(progress, data.referral_count, getDaysSinceJoined(createdAtMs));
          setUpdatedAtEpoch(convertToEpoch(data.updated_at));
          setCreatedAtEpoch(createdAtMs);
          setThumbAtEpoch(convertToEpoch(data.thumb_at));
          setCensoredEmail(data.censored_email ?? null);
          setDirectReferralCount(data.direct_referral_count);
          console.log("get_challenge.php name:", data.name);
          if (!cid) {
            navigate(`/challenge/${ownCid}`);
          }
        } else {
          console.log("get_challenge.php error:", data.error);
          if (data.error.startsWith("cid not found")) {
            localStorage.removeItem(KEY_OWN_CID);
            localStorage.removeItem(KEY_TOKEN);
            navigate("/challenge");
          }
        }
      })
      .catch(error => console.error('Error:', error));
  };

  useEffect(() => {
    loadChallenge();
  }, [cidToGet]);

  return {
    cid: cidToGet,
    name,
    progress,
    processedProgress,
    updatedAtEpoch,
    thumbAtEpoch,
    censoredEmail,
    referralCount,
    directReferralCount,
    refresh: loadChallenge
  };
};